/* eslint-disable max-len */
import { CommonModule, WeekDay } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { dayjs } from '@karve.it/core';
import { Store } from '@ngrx/store';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { RippleModule } from 'primeng/ripple';

import { SubSink } from 'subsink';

import { FreyaCommonModule } from "../../../../../app/freya-common/freya-common.module";
import { CalendarEvent, User } from '../../../../../generated/graphql.generated';
import { CAPABILITIES_BACKEND_MAP, EMAIL_PATTERN } from '../../../../global.constants';
import { PermissionService } from '../../../../services/permission.service';
import { TimezoneHelperService } from '../../../../services/timezone-helper.service';
import { SharedModule } from '../../../../shared/shared.module';
import { DispatchActions } from '../../store/dispatch.actions';


import { selectSelectedCrewSettingsTab, selectSelectedUserBookOffEvents, selectSelectedUserForRateEdit } from '../../store/dispatch.selectors';

import { defaultAvailability } from './default-crew-settings';

interface AvailabilityRow {
  day: string;
  isEnabled: boolean;
  start: Date;
  end: Date;
}

interface Capability {
  label: string;
  isChecked: boolean;
}

interface BookOffOption {
  label: string;
  start?: number;
  end?: number;
  custom?: boolean;
}

export interface BookOffInput {
  userId: string;
  selectedDate: Date | null;
  selectedTimeBlock: BookOffOption;
  description: string;
  start: number | null;
  end: number | null;
  customStartTime: Date | null;
  customEndTime: Date | null;
}

@Component({
  selector: 'app-dispatch-crew-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AccordionModule,
    DropdownModule,
    CheckboxModule,
    CalendarModule,
    ButtonModule,
    InputTextModule,
    InputTextareaModule,
    InputMaskModule,
    InputNumberModule,
    RippleModule,
    SharedModule,
    MultiSelectModule,
    FreyaCommonModule
],
  templateUrl: './dispatch-crew-settings.component.html',
  styleUrl: './dispatch-crew-settings.component.scss',
})
export class DispatchCrewSettingsComponent implements OnInit, OnDestroy {
  // D-8 (Maria): visible slider steps (0=Any ... 1=Top).
  performanceSliderSteps = ['Any', '0.2', '0.4', '0.6', '0.8', 'Top'];


  user: User;
  user$ = this.store.select(selectSelectedUserForRateEdit);

  @Output() dialogClosed = new EventEmitter<void>();

  AVAILABLE_TIMEZONES = this.timezoneHelper.availableTimezones.map(timezone => ({
    label: timezone.name,
    value: timezone.name,
  }));

  userBookOffs$ = this.store.select(selectSelectedUserBookOffEvents);

  showPastBookOffs = false;

  bookOffOptions: BookOffOption[] = [
    {
      label: 'All Day',
        start: 0,
        end: 86340
    },
    {
      label: 'Morning',
        start: 0,
        end: 43140,
    },
    {
      label: 'Afternoon',
        start: 43200,
        end: 86340,
    },
    {
      label: 'Custom',
      custom: true,
    }
  ];

  bookOffInput: BookOffInput = this.createDefaultBookOffInput();

  activeTab$ = this.store.select(selectSelectedCrewSettingsTab);

  payPeriodOptions = [
    { label: 'Sunday', value: 0 },
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 },
  ];

  onTabChange(value: number | number[]) {
    const index = Array.isArray(value) ? (value.length ? value[0] : null) : value;
    if (index !== null) {
      this.store.dispatch(DispatchActions.crewSettingsTabChanged({ index }));
    }
  }

  isAvailableChecked = false;
  availabilityRows: AvailabilityRow[] = [];
  private readonly defaultCustomStartHour = 9;
  private readonly defaultCustomEndHour = 17;

  allCapabilities: Capability[] = [];
  selectedCapabilities: Capability[] = [];

  private readonly displayDays = Object.keys(WeekDay).filter(key => isNaN(Number(key)));
  
  private readonly defaultStart = '08:00';
  private readonly defaultEnd = '17:00';

  DispatchActions = DispatchActions;
  subs = new SubSink();

  /** Performance score snap-point labels */
  private readonly performanceScoreLabelMap: Array<{ value: number; label: string }> = [
    { value: 0,   label: 'Any' },
    { value: 0.2, label: 'Basic' },
    { value: 0.4, label: 'Standard' },
    { value: 0.6, label: 'Skilled' },
    { value: 0.8, label: 'Expert' },
    { value: 1.0, label: 'Top' },
  ];

  /** Return a human-readable label for the given performance score value */
  getPerformanceScoreLabel(score: number | null | undefined): string {
    // Default to 0.4 (Standard) — matches the slider's 0.2 snap grid
    const raw = score ?? 0.4;
    // Snap to nearest 0.2
    const snapped = Math.round(raw / 0.2) * 0.2;
    const v = Math.min(1, Math.max(0, snapped));
    const snap = this.performanceScoreLabelMap.find((e) => Math.abs(e.value - v) < 0.01);
    return snap ? snap.label : v.toFixed(1);
  }

  /** Observable — true if the current user can view/edit performance scores */
  canManagePerformance$ = this.permissionService.watchPermission('dispatch.managePerformance');

  constructor(
    private store: Store,
    private timezoneHelper: TimezoneHelperService,
    private permissionService: PermissionService,
  ) {}
    
  ngOnInit() {
    this.subs.sink = this.user$
    .subscribe((user) => {
      this.user = user;
      if (this.user && !this.user.timezone && !this.user.id) {
        this.updateUser({ timezone: this.timezoneHelper.getCurrentTimezone() });
      }
      this.loadAvailability();
      this.syncAvailableChecked();
      this.loadSettingsInformation();
      this.resetBookOffForm();
    });
  }

  private syncAvailableChecked() {
    const hasAvailableDays = this.availabilityRows.some(row => row.isEnabled);
    if (this.user?.available && !hasAvailableDays) {
      this.isAvailableChecked = false;
      this.updateUser({ available: false });
    } else {
      this.isAvailableChecked = this.user?.available ?? false;
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // --- Event Handlers ---
  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(_event: KeyboardEvent) {
    this.onClose();
  }

  onClose() {
    this.store.dispatch(DispatchActions.editCrewSettingsDialogClosed());
  }

  onDateChanged(date: Date | null) {
    this.bookOffInput = {
      ...this.bookOffInput,
      selectedDate: date,
    };
    this.syncBookOffUnixRange();
  }

  onTimeBlockChanged(option: BookOffOption) {
    const needsDefaultCustomTimes = option?.custom && (!this.bookOffInput.customStartTime || !this.bookOffInput.customEndTime);
    const baseDate = this.bookOffInput.selectedDate ?? new Date();
    this.bookOffInput = {
      ...this.bookOffInput,
      selectedTimeBlock: option,
      customStartTime: option?.custom
        ? (needsDefaultCustomTimes ? this.buildTime(baseDate, this.defaultCustomStartHour) : this.bookOffInput.customStartTime)
        : null,
      customEndTime: option?.custom
        ? (needsDefaultCustomTimes ? this.buildTime(baseDate, this.defaultCustomEndHour) : this.bookOffInput.customEndTime)
        : null,
    };
    this.syncBookOffUnixRange();
  }

  onCustomTimeChanged() {
    this.syncBookOffUnixRange();
  }

  onDescriptionChanged(description: string) {
    this.bookOffInput = {
      ...this.bookOffInput,
      description,
    };
  }

  deleteBookOffEvent(id: string) {
    this.store.dispatch(DispatchActions.deleteCrewBookOffEvent({ bookOffId: id }));
  }

  get customTimeInvalid(): boolean {
    if (!this.bookOffInput.selectedTimeBlock?.custom) {
      return false;
    }
    const { customStartTime, customEndTime } = this.bookOffInput;
    if (!customStartTime || !customEndTime) {
      return true;
    }
    return customEndTime.getTime() <= customStartTime.getTime();
  }

  canSubmitBookOff(): boolean {
    return Boolean(
      this.bookOffInput.selectedDate
      && this.bookOffInput.start !== null
      && this.bookOffInput.end !== null
      && (!this.bookOffInput.selectedTimeBlock?.custom || !this.customTimeInvalid)
    );
  }

  bookOff() {
    if (!this.user?.id) {
      console.warn('No crew selected for book-off');
      return;
    }

    this.syncBookOffUnixRange();

    if (!this.canSubmitBookOff() || this.bookOffInput.start === null || this.bookOffInput.end === null) {
      console.warn('Book-off form is incomplete', this.bookOffInput);
      return;
    }

    const payload: BookOffInput = {
      ...this.bookOffInput,
      userId: this.user.id,
      description: this.bookOffInput.description?.trim() ?? '',
    };

    this.store.dispatch(DispatchActions.crewBookOffCreated({ newBookOff: payload }));
    this.resetBookOffForm();
  }

  loadAvailability() {
    const parseTimeRange = (timeString: string): Date => {
      const [hourStr, minuteStr] = timeString.split(':');
      const hours = parseInt(hourStr, 10);
      const minutes = parseInt(minuteStr, 10);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);

      return date;
    }

    const availabilityStrings = this.user?.availability || defaultAvailability;
    const parsedRows = availabilityStrings.map((hours, index) => {
      let isEnabled = false;
      let start = this.defaultStart;
      let end = this.defaultEnd;

      if (hours === "") {
        isEnabled = false;
      } else {
        try {
          if (hours[0] === 'N' || hours[0] === 'Y') {
            isEnabled = (hours[0] === 'Y');
          }
          const parts = hours.slice(1).split('-');
          if (parts.length === 2) {
            start = parts[0] || this.defaultStart;
            end = parts[1] || this.defaultEnd;
          }
        } catch (e) {
          console.error(`Error parsing availability string: "${hours}". Using defaults.`, e);
          // Fallback to defaults in case of any error
          isEnabled = false;
          start = this.defaultStart;
          end = this.defaultEnd;
        }
      }

      return {
        day: this.displayDays[index],
        isEnabled: isEnabled,
        start: parseTimeRange(start),
        end: parseTimeRange(end),
      };
    });

    this.availabilityRows = parsedRows;
  }

  formatDateToTime(date: Date | string) {
      if (typeof date === 'string') {
        const [timePart, ampm] = date.split(' ');
        const [hourString, minuteString] = timePart.split(':');
        let hours = parseInt(hourString, 10);

        if (ampm.toUpperCase() === 'PM') {
          if (hours !== 12) {
            hours += 12;
          }
        } else { 
          if (hours === 12) {
            hours = 0;
          }
        }

        const hourString24 = String(hours).padStart(2, '0');
        return `${hourString24}:${minuteString}`;
      }
      const dateObj = new Date(date);
      const hours = dateObj.getHours();
      const minutes = dateObj.getMinutes();

      const hoursString = String(hours).padStart(2, '0');
      const minutesString = String(minutes).padStart(2, '0');

      return `${hoursString}:${minutesString}`;
  }

  formatAvailability() {
    const availability = this.availabilityRows.map(row => {
      const dayStart = this.formatDateToTime(row.start) || this.defaultStart;
      const dayEnd = this.formatDateToTime(row.end) || this.defaultEnd;

      if (row.isEnabled) {
          return `Y${dayStart}-${dayEnd}`;
      } else {
        return `N${dayStart}-${dayEnd}`;
      }
    });

    return {
      availability,
    };
  }

  public onAvailableForAssignmentChange(event: any) {
    this.isAvailableChecked = event.checked;
    if (!event.checked) {
      this.updateUser({ available: false });
      return;
    }
    const hasAvailableDays = this.availabilityRows.some(row => row.isEnabled);
    if (hasAvailableDays) {
      this.updateUser({ available: true });
    } else {
      this.availabilityRows.forEach(row => { row.isEnabled = true; });
      const { availability } = this.formatAvailability();
      this.updateUser({ available: true, availability, timezone: this.resolveTimezone() });
    }
  }

  public onAvailabilityChange() {
    const { availability } = this.formatAvailability();
    const hasAvailableDays = this.availabilityRows.some(row => row.isEnabled);

    if (!hasAvailableDays) {
      this.isAvailableChecked = false;
    }

    const available = this.isAvailableChecked && hasAvailableDays;
    this.updateUser({ availability, timezone: this.resolveTimezone(), available });
  }

  // --- Book Off Methods ---
  formatBookOffEventDate(event: CalendarEvent): string {
    const startUnix = Number(event?.start);
    if (!Number.isFinite(startUnix)) {
      return '';
    }
    const timezone = this.resolveTimezone();
    const start = dayjs(startUnix * 1000).tz(timezone);
    const endUnix = Number(event?.end);
    if (!Number.isFinite(endUnix)) {
      return start.format('MMM D, YYYY');
    }
    const end = dayjs(endUnix * 1000).tz(timezone);
    if (start.isSame(end, 'day')) {
      return start.format('MMM D, YYYY');
    }
    return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
  }

  formatBookOffEventTime(event: CalendarEvent): string {
    const startUnix = Number(event?.start);
    const endUnix = Number(event?.end);
    if (!Number.isFinite(startUnix) || !Number.isFinite(endUnix)) {
      return '';
    }
    const timezone = this.resolveTimezone();
    const start = dayjs(startUnix * 1000).tz(timezone);
    const end = dayjs(endUnix * 1000).tz(timezone);
    const durationSeconds = end.unix() - start.unix();
    if (durationSeconds >= 86340) {
      return `All day`;
    }

    const formatMaybeMidnightEdgeCase = (d: dayjs.Dayjs) => {
      const hour = d.hour();
      const minute = String(d.minute()).padStart(2, '0');
      if (hour === 0) {
        return `00:${minute} AM`;
      }
      return d.format('h:mm A');
    };

    const startLabel = formatMaybeMidnightEdgeCase(start);
    const endLabel = formatMaybeMidnightEdgeCase(end);

    return `${startLabel} - ${endLabel}`;
  }

  trackBookOffEvent(_index: number, event: CalendarEvent) {
    return event?.id ?? _index;
  }

  isBookOffEventCurrentOrFuture(event: CalendarEvent): boolean {
    if (this.showPastBookOffs) {
      return true;
    }
    const endUnix = Number(event?.end ?? event?.start);
    if (!Number.isFinite(endUnix)) {
      return false;
    }
    const timezone = this.resolveTimezone();
    const startOfTodayUnix = dayjs().tz(timezone).startOf('day').unix();
    return endUnix >= startOfTodayUnix;
  }

  toggleShowPastBookOffs(): void {
    this.showPastBookOffs = true;
  }

  hasPastBookOffsIn(events: any[] | null | undefined): boolean {
    if (!events || events.length === 0) return false;
    const timezone = this.resolveTimezone();
    const startOfToday = dayjs().tz(timezone).startOf('day').unix();
    return events.some(ev => {
      const endUnix = Number(ev?.end ?? ev?.start);
      return Number.isFinite(endUnix) && endUnix < startOfToday;
    });
  }

  

  private resetBookOffForm() {
    const defaultInput = this.createDefaultBookOffInput();
    const userId = this.user?.id ?? defaultInput.userId;
    this.bookOffInput = {
      ...defaultInput,
      userId,
    };
    this.syncBookOffUnixRange();
  }

  private createDefaultBookOffInput(): BookOffInput {
    const baseDate = new Date();
    return {
      userId: this.user?.id ?? '',
      selectedDate: baseDate,
      selectedTimeBlock: this.bookOffOptions[0],
      description: '',
      start: null,
      end: null,
      customStartTime: this.buildTime(baseDate, this.defaultCustomStartHour),
      customEndTime: this.buildTime(baseDate, this.defaultCustomEndHour),
    };
  }

  private buildTime(baseDate: Date, hour: number, minute = 0): Date {
    const time = new Date(baseDate);
    time.setHours(hour, minute, 0, 0);
    return time;
  }

  private syncBookOffUnixRange(): void {
    if (!this.bookOffInput.selectedDate) {
      this.bookOffInput = {
        ...this.bookOffInput,
        start: null,
        end: null,
      };
      return;
    }

    const timezone = this.resolveTimezone();
    const selectedDate = this.bookOffInput.selectedDate;

    if (this.bookOffInput.selectedTimeBlock?.custom) {
      const { customStartTime, customEndTime } = this.bookOffInput;
      if (!customStartTime || !customEndTime) {
        this.bookOffInput = {
          ...this.bookOffInput,
          start: null,
          end: null,
        };
        return;
      }

      const start = dayjs(selectedDate)
        .tz(timezone, true)
        .set('hour', customStartTime.getHours())
        .set('minute', customStartTime.getMinutes())
        .set('second', 0)
        .unix();

      const end = dayjs(selectedDate)
        .tz(timezone, true)
        .set('hour', customEndTime.getHours())
        .set('minute', customEndTime.getMinutes())
        .set('second', 0)
        .unix();

      this.bookOffInput = {
        ...this.bookOffInput,
        start,
        end,
      };
      return;
    }

    const blockStart = this.bookOffInput.selectedTimeBlock?.start ?? 0;
    const blockEnd = this.bookOffInput.selectedTimeBlock?.end ?? blockStart;

    const start = dayjs(selectedDate)
      .tz(timezone, true)
      .startOf('day')
      .add(blockStart, 'seconds')
      .unix();

    const end = dayjs(selectedDate)
      .tz(timezone, true)
      .startOf('day')
      .add(blockEnd, 'seconds')
      .unix();

    this.bookOffInput = {
      ...this.bookOffInput,
      start,
      end,
    };
  }

  private resolveTimezone(): string {
    return this.user?.timezone || this.timezoneHelper.getCurrentTimezone();
  }

  loadSettingsInformation() {
    const backendCaps = new Set(this.user?.capabilities);
    this.allCapabilities = Object.keys(CAPABILITIES_BACKEND_MAP).map(label => {
      return {
        label: label,
        isChecked: backendCaps.has(CAPABILITIES_BACKEND_MAP[label])
      };
    });
    this.selectedCapabilities = this.allCapabilities.filter(cap => cap.isChecked);
  }

  onCapabilitiesChange() {
    this.allCapabilities.forEach(cap => cap.isChecked = false);
    this.selectedCapabilities.forEach(selected => {
      this.allCapabilities.find(cap => cap.label === selected.label).isChecked = true;
    });

    const dataToSave = this.allCapabilities
      .filter(cap => cap.isChecked)
      .map(cap => cap.label).sort((a, b) => a.localeCompare(b))
      .map(cap => CAPABILITIES_BACKEND_MAP[cap]);
    
    this.updateUser({ capabilities: dataToSave });
  }

  isPhoneInvalid = false;

  onPhoneChange(value: string) {
    const phone = (value ?? '').toString().replace(/\D/g, '');

    if (!value) {
      this.isPhoneInvalid = false;
    } else {
      this.isPhoneInvalid = phone.length > 0 && phone.length < 10;
    }

    if (this.isPhoneInvalid) {
      return;
    }

    this.updateUser({ phone });

  }

  isEmailInvalid = false;

  onEmailChange(value: string) {
    const email = value ?? '';
    
    if (email !== '' && !EMAIL_PATTERN.test(email)) {
      this.isEmailInvalid = true;
      return;
    }

    this.isEmailInvalid = false;
    this.updateUser({ email });
  }

  onTimezoneChanged(timezone: string | null) {
    if (!timezone) {
      return;
    }

    this.updateUser({ timezone });
  }

  updateUser(changes: Partial<User>) {
    if (this.user?.id) {
      this.store.dispatch(DispatchActions.crewSettingsUpdated({
        id: this.user.id,
        ...changes
      }));
    } else {
      this.store.dispatch(DispatchActions.updateDraftCrewMember({ user: changes }));
    }
  }
}
